/**
 * Insole3D - Three.js 3D insole posture visualization.
 *
 * Renders a flattened box that approximates an insole and tilts it according
 * to the incoming quaternion. Four view presets (Front / Side / Top / Free)
 * are provided; selecting one animates the camera to the preset position over
 * 30 frames with ease-in-out. OrbitControls allow free rotation in any preset.
 *
 * Performance optimizations (Phase 7.3):
 *   - Geometry and material wrapped in `useMemo` to avoid recreation.
 *   - On-demand rendering ("frameloop: demand"): the rAF loop calls
 *     `controls.update()` every frame (for damping) but only calls
 *     `renderer.render()` when the `needsRender` flag is set. The flag is
 *     raised on quaternion / preset / resize changes and on OrbitControls
 *     `change` events. This eliminates idle GPU draws.
 *   - BoxGeometry(28, 4, 70) = 24 vertices, well under the 5000-vertex target.
 *
 * Lifecycle: scene/camera/renderer/controls are created in a mount-time
 * effect and disposed on unmount. Quaternion, viewPreset, and size updates are
 * applied through refs without recreating the scene. If the WebGL context is
 * lost, a static fallback image is shown.
 *
 * Performance target: single-frame render P95 ≤ 6ms.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ViewPreset } from '../../types/enums';
import type { Quaternion } from '../../types/ski-data';

export interface Insole3DProps {
  quaternion: Quaternion;
  width: number;
  height: number;
  viewPreset: ViewPreset;
}

// Camera positions for each view preset (looking at origin).
const PRESET_CAMERAS: Record<ViewPreset, THREE.Vector3> = {
  [ViewPreset.Front]: new THREE.Vector3(0, 0, 180),
  [ViewPreset.Side]: new THREE.Vector3(180, 0, 0),
  [ViewPreset.Top]: new THREE.Vector3(0, 220, 0.001),
  [ViewPreset.Free]: new THREE.Vector3(120, 90, 120),
};

const TRANSITION_FRAMES = 30;

/** Map a 0..1 progress value to an ease-in-out curve (cubic). */
function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export default function Insole3D({ quaternion, width, height, viewPreset }: Insole3DProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const [contextLost, setContextLost] = useState(false);
  // Internal active preset (driven by both the prop and the preset buttons).
  const [activePreset, setActivePreset] = useState<ViewPreset>(viewPreset);

  // Memoized geometry and material (avoid recreation on re-render).
  // BoxGeometry(28, 4, 70) with default 1×1×1 segments → 24 vertices, 12
  // triangles — well under the 5000-vertex-per-insole target.
  const insoleGeo = useMemo(() => new THREE.BoxGeometry(28, 4, 70), []);
  const insoleMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: 0x1c1c28,
        metalness: 0.2,
        roughness: 0.6,
        emissive: 0x0a0a0f,
      }),
    []
  );
  const edgesGeo = useMemo(() => new THREE.EdgesGeometry(insoleGeo), [insoleGeo]);
  const lineMat = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: 0x22d3ee,
        transparent: true,
        opacity: 0.5,
      }),
    []
  );

  // Dispose memoized GPU resources on unmount.
  useEffect(() => {
    return () => {
      insoleGeo.dispose();
      insoleMat.dispose();
      edgesGeo.dispose();
      lineMat.dispose();
    };
  }, [insoleGeo, insoleMat, edgesGeo, lineMat]);

  // Persistent handles to Three.js objects (created once on mount).
  const sceneRef = useRef<{
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    controls: OrbitControls;
    insole: THREE.Mesh;
    rafId: number;
    needsRender: boolean;
    // Camera transition state.
    camFrom: THREE.Vector3;
    camTo: THREE.Vector3;
    transitionFrame: number;
  } | null>(null);

  // ---- Mount: build the scene once ----
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    // WebGL context loss → show fallback.
    const handleContextLost = (event: Event) => {
      event.preventDefault();
      setContextLost(true);
    };
    renderer.domElement.addEventListener('webglcontextlost', handleContextLost);

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x0a0a0f, 200, 600);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 2000);
    camera.position.copy(PRESET_CAMERAS[viewPreset]);
    camera.lookAt(0, 0, 0);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.target.set(0, 0, 0);

    // Lighting: ambient + directional.
    const ambient = new THREE.AmbientLight(0x6688aa, 0.7);
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 0.9);
    dir.position.set(80, 120, 100);
    scene.add(dir);
    const fill = new THREE.DirectionalLight(0x22d3ee, 0.3);
    fill.position.set(-80, 40, -60);
    scene.add(fill);

    // Insole mesh: uses the memoized geometry + material.
    const insole = new THREE.Mesh(insoleGeo, insoleMat);
    scene.add(insole);

    // Subtle outline edges for readability.
    const wireframe = new THREE.LineSegments(edgesGeo, lineMat);
    insole.add(wireframe);

    // Grid plane for spatial reference.
    const grid = new THREE.GridHelper(300, 20, 0x223344, 0x111822);
    (grid.material as THREE.Material).transparent = true;
    (grid.material as THREE.Material).opacity = 0.3;
    grid.position.y = -20;
    scene.add(grid);

    // Camera transition state (no active transition at start).
    const camFrom = camera.position.clone();
    const camTo = camera.position.clone();
    let transitionFrame = -1; // -1 means idle

    // On-demand rendering: needsRender is raised on any state change.
    // The rAF loop calls controls.update() every frame (for damping) but
    // only calls renderer.render() when needsRender is true.
    let needsRender = true; // initial render

    const handle = {
      renderer,
      scene,
      camera,
      controls,
      insole,
      rafId: 0,
      needsRender,
      camFrom,
      camTo,
      transitionFrame,
    };

    // OrbitControls 'change' event → raise needsRender (user drag / damping).
    const onControlsChange = () => {
      handle.needsRender = true;
    };
    controls.addEventListener('change', onControlsChange);

    const animate = () => {
      handle.rafId = requestAnimationFrame(animate);

      // Animate camera transition (30-frame ease-in-out).
      if (handle.transitionFrame >= 0 && handle.transitionFrame < TRANSITION_FRAMES) {
        const t = easeInOut((handle.transitionFrame + 1) / TRANSITION_FRAMES);
        camera.position.lerpVectors(handle.camFrom, handle.camTo, t);
        camera.lookAt(0, 0, 0);
        handle.transitionFrame++;
        handle.needsRender = true; // transition needs rendering
        if (handle.transitionFrame >= TRANSITION_FRAMES) {
          handle.transitionFrame = -1; // transition complete
        }
      }

      // controls.update() is needed every frame for damping to settle.
      // It fires 'change' (→ needsRender) while damping is active.
      controls.update();

      // On-demand render: skip renderer.render() when nothing changed.
      if (handle.needsRender) {
        renderer.render(scene, camera);
        handle.needsRender = false;
      }
    };
    animate();

    sceneRef.current = handle;

    return () => {
      cancelAnimationFrame(handle.rafId);
      controls.removeEventListener('change', onControlsChange);
      controls.dispose();
      renderer.domElement.removeEventListener('webglcontextlost', handleContextLost);
      renderer.dispose();
      // Dispose grid material (grid geometry is shared/internal).
      (grid.material as THREE.Material).dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
      sceneRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Apply quaternion updates ----
  useEffect(() => {
    const handle = sceneRef.current;
    if (!handle) return;
    handle.insole.quaternion.set(quaternion.x, quaternion.y, quaternion.z, quaternion.w);
    handle.needsRender = true; // state changed → render next frame
  }, [quaternion]);

  // ---- Sync prop → internal state ----
  useEffect(() => {
    setActivePreset(viewPreset);
  }, [viewPreset]);

  // ---- Apply view preset changes (start a camera transition) ----
  useEffect(() => {
    const handle = sceneRef.current;
    if (!handle) return;
    handle.camFrom.copy(handle.camera.position);
    handle.camTo.copy(PRESET_CAMERAS[activePreset]);
    handle.transitionFrame = 0;
    handle.needsRender = true; // transition starts → render
  }, [activePreset]);

  // ---- Handle resize ----
  useEffect(() => {
    const handle = sceneRef.current;
    if (!handle) return;
    handle.renderer.setSize(width, height);
    handle.camera.aspect = width / height;
    handle.camera.updateProjectionMatrix();
    handle.needsRender = true; // resize → re-render
  }, [width, height]);

  // ---- View preset buttons ----
  const buttonStyle = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px',
    fontSize: 12,
    border: `1px solid ${active ? 'var(--color-ice)' : 'var(--border-default)'}`,
    borderRadius: 6,
    background: active ? 'rgba(34, 211, 238, 0.12)' : 'transparent',
    color: active ? 'var(--color-ice)' : 'var(--text-secondary)',
    cursor: 'pointer',
    transition: 'all 150ms ease',
  });

  const presets: ViewPreset[] = [ViewPreset.Front, ViewPreset.Side, ViewPreset.Top, ViewPreset.Free];
  const presetLabels: Record<ViewPreset, string> = {
    [ViewPreset.Front]: '正面',
    [ViewPreset.Side]: '侧面',
    [ViewPreset.Top]: '俯视',
    [ViewPreset.Free]: '自由',
  };

  if (contextLost) {
    // WebGL fallback: static posture image (simple inline SVG).
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          color: 'var(--text-tertiary)',
          background: 'var(--bg-card)',
          borderRadius: 12,
        }}
      >
        <svg width={Math.min(120, width - 40)} height={Math.min(80, height - 60)} viewBox="0 0 120 80">
          <rect x="46" y="10" width="28" height="60" rx="10" fill="#1C1C28" stroke="#22D3EE" strokeWidth="1.5" opacity="0.8" />
          <line x1="10" y1="74" x2="110" y2="74" stroke="#6B6B80" strokeWidth="1" />
        </svg>
        <span style={{ fontSize: 12 }}>WebGL 不可用，显示静态姿态</span>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width, height }}>
      <div ref={mountRef} style={{ width, height }} />
      <div
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          display: 'flex',
          gap: 4,
          zIndex: 2,
        }}
      >
        {presets.map((p) => (
          <button
            key={p}
            type="button"
            style={buttonStyle(activePreset === p)}
            onClick={() => setActivePreset(p)}
          >
            {presetLabels[p]}
          </button>
        ))}
      </div>
    </div>
  );
}
